import type { Position } from '@rio-cloud/rio-uikit/mapTypes';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useRef } from 'react';

import { type FetchHereGeocodePositionProps, fetchHereGeocodePosition } from '#utils/here/fetchHereGeocodePosition.ts';

import type { Location } from './OrderFormValidation.ts';

type useDebouncedGeocodingProps = {
    location?: Location;
    setPosition: (position: Position | undefined) => void;
};

const GEOCODE_DEBOUNCE_MS = 400;

export const useDebouncedGeocoding = ({ location, setPosition }: useDebouncedGeocodingProps) => {
    const geocodeRequestRef = useRef(0);
    const setPositionRef = useRef(setPosition);
    setPositionRef.current = setPosition;

    const debouncedGeocode = useMemo(
        () =>
            debounce(async (props: FetchHereGeocodePositionProps, requestId: number) => {
                const position = await fetchHereGeocodePosition(props);
                if (requestId !== geocodeRequestRef.current) {
                    return;
                }

                setPositionRef.current(position ? { lat: position?.latitude, lng: position?.longitude } : undefined);
            }, GEOCODE_DEBOUNCE_MS),
        []
    );

    useEffect(() => {
        return () => {
            debouncedGeocode.cancel();
        };
    }, [debouncedGeocode]);

    useEffect(() => {
        const requestId = ++geocodeRequestRef.current;

        // Currently, the address is not editable and the coordinates are from the address search.
        // So if there are coordinates, we can skip the geocode request.
        // If the address becomes editable, this check can be removed and the geocode request can be made for every location change
        if (location?.latitude !== undefined && location?.longitude !== undefined) {
            debouncedGeocode.cancel();
            setPosition({ lat: location.latitude, lng: location.longitude });
            return;
        }

        if (!(location?.street && location?.city && location?.country && location?.zipCode)) {
            debouncedGeocode.cancel();
            setPosition(undefined);
            return;
        }

        debouncedGeocode(location, requestId);
    }, [
        location?.latitude,
        location?.longitude,
        location?.street,
        location?.city,
        location?.country,
        location?.zipCode,
        debouncedGeocode,
    ]);
};
